"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { formatTimeDisplay } from "@/lib/utils/time";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Settings,
  Plus,
  Trash2,
  Users,
  UserCog,
  Loader2,
  AlertTriangle,
  Check,
} from "lucide-react";
import { STATUS_IDS } from "@/lib/constants/statuses";
import { ROLES } from "@/lib/constants/roles";
import { fetchTimeSlotPricingByRole } from "@/lib/api/time-slot-pricing";
import { getCurrencySymbol } from "@/lib/utils/currency";
import { toast } from "sonner";

interface PricePoint {
  id: number;
  amount?: number; // For backwards compatibility
  label?: string; // For backwards compatibility
  currency?: string; // For backwards compatibility
  vat_percentage?: number; // For backwards compatibility
  vatPercentage?: number; // For backwards compatibility
  include_vat?: boolean;
  includeVat?: boolean;
  created_at?: string;
  createdAt?: string;
  status_id?: number;
  pricing_id?: number;
  pricing?: {
    id: number;
    price: number;
    label?: string;
    currency?: string;
    vat_percentage: number;
  };
}

interface SlotPricing {
  timeSlotId: string;
  pricePointId: number | null;
  dayOfWeek: number;
  user_role_id?: number;
}

interface TimeSlotPricingTabProps {
  pricePoints: PricePoint[];
  selectedPricePoint: number | null;
  onPricePointSelect: (id: number | null) => void;
  onPricePointCreate: (pricePoint: Omit<PricePoint, "id">) => void;
  onPricePointDelete: (id: number) => void;
  onPricePointStatusUpdate?: (id: number, statusId: number) => void;
  onSlotPricingUpdate: (slotPricing: SlotPricing[]) => void;
  onSave?: () => void;
  isSaving?: boolean;
  savingSlots?: Set<string>;
  uniqueTimes: string[];
  venueId: number;
  showId: number;
  venueCurrency?: string;
  venueDefaultLanguage: string;
  userRoleId?: number;
  onUserRoleIdChange?: (roleId: number) => void;
}

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getDayName = (dayIndex: number): string => {
  return DAYS_OF_WEEK[dayIndex] || `Day ${dayIndex}`;
};

const COLOR_PALETTE = [
  { bg: "bg-blue-100", border: "border-blue-500", text: "text-blue-700" },
  { bg: "bg-green-100", border: "border-green-500", text: "text-green-700" },
  { bg: "bg-purple-100", border: "border-purple-500", text: "text-purple-700" },
  { bg: "bg-yellow-100", border: "border-yellow-500", text: "text-yellow-700" },
  { bg: "bg-pink-100", border: "border-pink-500", text: "text-pink-700" },
  { bg: "bg-indigo-100", border: "border-indigo-500", text: "text-indigo-700" },
  { bg: "bg-red-100", border: "border-red-500", text: "text-red-700" },
  { bg: "bg-orange-100", border: "border-orange-500", text: "text-orange-700" },
];

export function TimeSlotPricingTab({
  venueCurrency,
  venueDefaultLanguage,
  pricePoints,
  selectedPricePoint,
  onPricePointSelect,
  onPricePointCreate,
  onPricePointDelete,
  onPricePointStatusUpdate,
  onSlotPricingUpdate,
  onSave,
  isSaving = false,
  savingSlots = new Set(),
  uniqueTimes,
  venueId,
  showId,
  userRoleId: userRoleIdProp,
  onUserRoleIdChange,
}: TimeSlotPricingTabProps) {
  // console.log('TimeSlotPricingTab render - pricePoints count:', pricePoints.length);

  // State for slot pricing data
  const [slotPricing, setSlotPricing] = useState<SlotPricing[]>([]);
  const [staffSlotPricing, setStaffSlotPricing] = useState<SlotPricing[]>([]);
  const [guestSlotPricing, setGuestSlotPricing] = useState<SlotPricing[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSwitchingRole, setIsSwitchingRole] = useState(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);

  const [newPriceAmount, setNewPriceAmount] = useState("");
  const [newPriceLabel, setNewPriceLabel] = useState("");
  const [vatPercentage, setVatPercentage] = useState("25");
  const [includeVat, setIncludeVat] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [dragSelection, setDragSelection] = useState<Set<string>>(new Set());
  const [dragStartCell, setDragStartCell] = useState<{
    day: number;
    time: string;
  } | null>(null);
  const [isAddPriceModalOpen, setIsAddPriceModalOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    pricePoint: PricePoint | null;
  }>({
    isOpen: false,
    pricePoint: null,
  });
  // Role is controlled by parent when provided so it survives tab switches.
  // Falls back to local state for backwards compatibility.
  const [localUserRoleId, setLocalUserRoleId] = useState<number>(
    userRoleIdProp ?? ROLES.BOOKING_STAFF,
  );
  const userRoleId = userRoleIdProp ?? localUserRoleId;
  const setUserRoleId = (roleId: number) => {
    if (onUserRoleIdChange) {
      onUserRoleIdChange(roleId);
    } else {
      setLocalUserRoleId(roleId);
    }
  };

  // console.log('🚀 TimeSlotPricingTab initialized with role:', userRoleId, 'which is', userRoleId === ROLES.GUEST_USER ? 'GUEST' : 'STAFF');

  // State for price mismatch dialog
  const [priceMismatchDialog, setPriceMismatchDialog] = useState<{
    isOpen: boolean;
    slotInfo: { dayOfWeek: number; time: string; timeSlotId: string } | null;
    staffPricePointId: number | null;
    guestPricePointId: number | null;
  }>({
    isOpen: false,
    slotInfo: null,
    staffPricePointId: null,
    guestPricePointId: null,
  });

  // Function to fetch slot pricing for the selected role
  const fetchSlotPricingForRole = async (roleId: number) => {
    // console.log(`🔄 Fetching slot pricing for role ${roleId} (venue: ${venueId}, show: ${showId})`);
    setIsLoadingSlots(true);
    setLoadingError(null);

    try {
      const response = await fetchTimeSlotPricingByRole(
        venueId,
        showId,
        roleId,
      );
      // console.log('📥 API Response:', response);

      // Log deduplication info if available
      if (response.originalCount && response.originalCount > response.count) {
        // console.log(`🔧 Deduplication: ${response.originalCount} → ${response.count} slots (removed ${response.originalCount - response.count} duplicates)`);
      }

      // Transform API data to match the expected SlotPricing interface
      const transformedSlots: SlotPricing[] = response.slotPricing.map(
        (slot) => ({
          timeSlotId: slot.timeSlotId,
          pricePointId: slot.pricePointId,
          dayOfWeek: slot.dayOfWeek,
          user_role_id: slot.user_role_id,
        }),
      );

      // console.log(`✅ Setting ${transformedSlots.length} slots for role ${roleId}`);
      // console.log('📋 Sample transformed slots:', transformedSlots.slice(0, 3));
      setSlotPricing(transformedSlots);

      // Fetch the other role's pricing for comparison
      if (roleId === ROLES.GUEST_USER) {
        // If we're in guest view, fetch staff pricing for comparison
        try {
          const staffResponse = await fetchTimeSlotPricingByRole(
            venueId,
            showId,
            ROLES.BOOKING_STAFF,
          );
          const transformedStaffSlots: SlotPricing[] =
            staffResponse.slotPricing.map((slot) => ({
              timeSlotId: slot.timeSlotId,
              pricePointId: slot.pricePointId,
              dayOfWeek: slot.dayOfWeek,
              user_role_id: slot.user_role_id,
            }));
          setStaffSlotPricing(transformedStaffSlots);
        } catch (staffError) {
          console.error(
            "❌ Error fetching staff pricing for comparison:",
            staffError,
          );
          setStaffSlotPricing([]);
        }
      } else if (roleId === ROLES.BOOKING_STAFF) {
        // If we're in staff view, fetch guest pricing for comparison
        try {
          const guestResponse = await fetchTimeSlotPricingByRole(
            venueId,
            showId,
            ROLES.GUEST_USER,
          );
          const transformedGuestSlots: SlotPricing[] =
            guestResponse.slotPricing.map((slot) => ({
              timeSlotId: slot.timeSlotId,
              pricePointId: slot.pricePointId,
              dayOfWeek: slot.dayOfWeek,
              user_role_id: slot.user_role_id,
            }));
          setGuestSlotPricing(transformedGuestSlots);
        } catch (guestError) {
          console.error(
            "❌ Error fetching guest pricing for comparison:",
            guestError,
          );
          setGuestSlotPricing([]);
        }
      }
    } catch (error) {
      console.error("❌ Error fetching slot pricing:", error);
      setLoadingError(
        error instanceof Error ? error.message : "Failed to fetch slot pricing",
      );
      setSlotPricing([]);
    } finally {
      setIsLoadingSlots(false);
      setIsSwitchingRole(false);
    }
  };

  // Effect to fetch slot pricing when component mounts or role changes
  useEffect(() => {
    // console.log('📍 useEffect triggered for role:', userRoleId);
    fetchSlotPricingForRole(userRoleId);
  }, [userRoleId, venueId, showId]);

  // Handle role change
  const handleRoleChange = (newRoleId: string) => {
    const roleId = parseInt(newRoleId);
    // console.log('🔄 Role changing from', userRoleId, 'to', roleId);
    setIsSwitchingRole(true);
    setUserRoleId(roleId);
    // Clear comparison pricing data
    setStaffSlotPricing([]);
    setGuestSlotPricing([]);
    // fetchSlotPricingForRole will be called by useEffect
  };

  // Since we're fetching role-specific data, we don't need to filter further
  const filteredSlotPricing = slotPricing;

  // Internal function to handle slot pricing updates
  const updateSlotPricing = async (updatedSlots: SlotPricing[]) => {
    const previousSlots = [...slotPricing];

    // Calculate actual changes by comparing slot data
    let addedCount = 0;
    let updatedCount = 0;
    let removedCount = 0;

    // Check for new and updated slots
    updatedSlots.forEach((updatedSlot) => {
      const existingSlot = slotPricing.find(
        (s) => s.timeSlotId === updatedSlot.timeSlotId,
      );
      if (!existingSlot) {
        addedCount++;
      } else if (existingSlot.pricePointId !== updatedSlot.pricePointId) {
        updatedCount++;
      }
    });

    // Check for removed slots
    slotPricing.forEach((existingSlot) => {
      const stillExists = updatedSlots.find(
        (s) => s.timeSlotId === existingSlot.timeSlotId,
      );
      if (!stillExists) {
        removedCount++;
      }
    });

    const totalChanges = addedCount + updatedCount + removedCount;

    console.log("📝 updateSlotPricing called:", {
      previousCount: slotPricing.length,
      updatedCount: updatedSlots.length,
      changes: {
        added: addedCount,
        updated: updatedCount,
        removed: removedCount,
        total: totalChanges,
      },
      firstUpdated: updatedSlots[0],
    });

    // Log specific changes for debugging
    if (updatedCount > 0) {
      console.log(
        "🔄 Updated slots detected:",
        updatedSlots
          .filter((updatedSlot) => {
            const existingSlot = slotPricing.find(
              (s) => s.timeSlotId === updatedSlot.timeSlotId,
            );
            return (
              existingSlot &&
              existingSlot.pricePointId !== updatedSlot.pricePointId
            );
          })
          .map((slot) => ({
            timeSlotId: slot.timeSlotId,
            oldPrice: slotPricing.find((s) => s.timeSlotId === slot.timeSlotId)
              ?.pricePointId,
            newPrice: slot.pricePointId,
          })),
      );
    }

    try {
      // Update local state immediately
      setSlotPricing(updatedSlots);

      // Call the parent's save function (which will handle the API call)
      await onSlotPricingUpdate(updatedSlots);

      // Show success message with correct count
      // console.log(`✅ Successfully ${action} ${changedCount} time slot${changedCount > 1 ? 's' : ''}`);
    } catch (error) {
      console.error("Error updating slot pricing:", error);
      // Revert on error
      setSlotPricing(previousSlots);
      throw error;
    }
  };

  // Log the fetched results
  // console.log('Current TimeSlotPricingTab state:', {
  //   slotPricingLength: slotPricing.length,
  //   selectedUserRoleId: userRoleId,
  //   isLoading: isLoadingSlots,
  //   hasError: !!loadingError,
  //   firstFewSlots: slotPricing.slice(0, 3),
  //   allRoleIds: [...new Set(slotPricing.map(s => s.user_role_id))]
  // });

  // Sort price points: active ones first by creation date (newest first), then archived
  const sortedPricePoints = [...pricePoints].sort((a, b) => {
    const aStatusId = a.status_id || STATUS_IDS.PRICE_POINT_ACTIVE;
    const bStatusId = b.status_id || STATUS_IDS.PRICE_POINT_ACTIVE;

    // First, separate active from archived
    const aIsActive = aStatusId === STATUS_IDS.PRICE_POINT_ACTIVE;
    const bIsActive = bStatusId === STATUS_IDS.PRICE_POINT_ACTIVE;

    if (aIsActive && !bIsActive) return -1;
    if (!aIsActive && bIsActive) return 1;

    // Within the same status group, sort by creation date (newest first)
    const aDate = new Date(a.created_at || a.createdAt || 0).getTime();
    const bDate = new Date(b.created_at || b.createdAt || 0).getTime();
    return bDate - aDate;
  });

  const formatPrice = (amount: number, currency = venueCurrency) => {
    // Map currency to appropriate locale
    const currencyLocaleMap: { [key: string]: string } = {
      SEK: "sv-SE",
      NOK: "no-NO",
      DKK: "da-DK",
      EUR: "de-DE",
      USD: "en-US",
      GBP: "en-GB",
      KRW: "ko-KR",
      JPY: "ja-JP",
      CHF: "de-CH",
    };

    const locale = currencyLocaleMap[currency || "EUR"] || "en-US";

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getSlotPrice = (
    dayOfWeek: number,
    time: string,
  ): SlotPricing | null => {
    const timeSlotId = `${dayOfWeek}_${time}`;
    return (
      filteredSlotPricing.find((sp) => sp.timeSlotId === timeSlotId) || null
    );
  };

  // Function to check if staff has a different price for this slot
  const checkStaffPriceForSlot = (
    dayOfWeek: number,
    time: string,
  ): number | null => {
    const timeSlotId = `${dayOfWeek}_${time}`;
    const staffSlot = staffSlotPricing.find(
      (slot) => slot.timeSlotId === timeSlotId,
    );
    return staffSlot ? staffSlot.pricePointId : null;
  };

  // Function to check if guest has a different price for this slot
  const checkGuestPriceForSlot = (
    dayOfWeek: number,
    time: string,
  ): number | null => {
    const timeSlotId = `${dayOfWeek}_${time}`;
    const guestSlot = guestSlotPricing.find(
      (slot) => slot.timeSlotId === timeSlotId,
    );
    return guestSlot ? guestSlot.pricePointId : null;
  };

  const handleCellClick = (dayOfWeek: number, time: string) => {
    const timeSlotId = `${dayOfWeek}_${time}`;
    const currentPrice = getSlotPrice(dayOfWeek, time);

    console.log("🖱️ Cell clicked:", {
      timeSlotId,
      currentPrice,
      currentPricePointId: currentPrice?.pricePointId,
      selectedPricePoint,
      userRoleId,
      isGuestView: userRoleId === ROLES.GUEST_USER,
      willUpdate: !!(currentPrice && selectedPricePoint),
      willSetNull: !!(currentPrice && !selectedPricePoint),
      willCreate: !!(!currentPrice && selectedPricePoint),
    });

    if (currentPrice && selectedPricePoint) {
      // Update existing slot with new price point (whether it currently has a price or is null)
      console.log("🔄 Updating slot price:", {
        timeSlotId,
        oldPricePointId: currentPrice.pricePointId,
        newPricePointId: selectedPricePoint,
        isSamePrice: currentPrice.pricePointId === selectedPricePoint,
      });

      // If clicking on a slot with the same price, toggle it off (set to NULL)
      if (currentPrice.pricePointId === selectedPricePoint) {
        console.log("🔄 Toggling off price (same price clicked)");
        const updatedSlots = slotPricing.map((sp) =>
          sp.timeSlotId === timeSlotId ? { ...sp, pricePointId: null } : sp,
        );
        updateSlotPricing(updatedSlots);
        return;
      }

      // Check for price mismatches with other role before updating
      if (userRoleId === ROLES.GUEST_USER) {
        // Guest trying to update price - check if staff has a different price
        const staffPricePointId = checkStaffPriceForSlot(dayOfWeek, time);

        if (staffPricePointId && staffPricePointId !== selectedPricePoint) {
          // Show dialog about price mismatch
          setPriceMismatchDialog({
            isOpen: true,
            slotInfo: { dayOfWeek, time, timeSlotId },
            staffPricePointId,
            guestPricePointId: selectedPricePoint,
          });
          return; // Don't update the price yet
        }
      } else if (userRoleId === ROLES.BOOKING_STAFF) {
        // Staff trying to update price - check if guest has a different price
        const guestPricePointId = checkGuestPriceForSlot(dayOfWeek, time);

        if (guestPricePointId && guestPricePointId !== selectedPricePoint) {
          // For staff, we could show a warning but allow them to proceed
          console.warn("⚠️ Price mismatch warning: Guest has different price", {
            guestPricePointId,
            selectedStaffPrice: selectedPricePoint,
          });
        }
      }

      const updatedSlots = slotPricing.map((sp) =>
        sp.timeSlotId === timeSlotId
          ? { ...sp, pricePointId: selectedPricePoint }
          : sp,
      );
      updateSlotPricing(updatedSlots);
    } else if (currentPrice && !selectedPricePoint) {
      // User clicked on slot with no price selected - set price_point_id to NULL
      const updatedSlots = slotPricing.map((sp) =>
        sp.timeSlotId === timeSlotId ? { ...sp, pricePointId: null } : sp,
      );
      updateSlotPricing(updatedSlots);
    } else if (!currentPrice && selectedPricePoint) {
      // Adding new price - check for mismatches with other role
      if (userRoleId === ROLES.GUEST_USER) {
        // Guest setting price - check if staff has a price set first
        const staffPricePointId = checkStaffPriceForSlot(dayOfWeek, time);

        if (!staffPricePointId) {
          // No staff price set - don't allow guest to set price
          toast.error(
            "Staff/Admin price must be set first before setting Guest/Customer price",
          );
          return;
        }

        if (staffPricePointId && staffPricePointId !== selectedPricePoint) {
          // Show dialog about price mismatch
          setPriceMismatchDialog({
            isOpen: true,
            slotInfo: { dayOfWeek, time, timeSlotId },
            staffPricePointId,
            guestPricePointId: selectedPricePoint,
          });
          return; // Don't set the price yet
        }
      } else if (userRoleId === ROLES.BOOKING_STAFF) {
        // Staff setting price - check if guest has a different price
        const guestPricePointId = checkGuestPriceForSlot(dayOfWeek, time);

        if (guestPricePointId && guestPricePointId !== selectedPricePoint) {
          // For staff, we could show a warning but allow them to proceed
          console.warn("⚠️ Price mismatch warning: Guest has different price", {
            guestPricePointId,
            selectedStaffPrice: selectedPricePoint,
          });
          // Optionally show a confirmation dialog
        }
      }

      // Assign price if no mismatch or allowed to proceed
      const newSlotPricing: SlotPricing = {
        timeSlotId,
        pricePointId: selectedPricePoint,
        dayOfWeek,
        user_role_id: userRoleId,
      };
      const updatedSlots = [...slotPricing, newSlotPricing];
      updateSlotPricing(updatedSlots);
    }
  };

  const getCellsInSelection = (
    start: { day: number; time: string },
    end: { day: number; time: string },
  ) => {
    const cells = new Set<string>();
    // Days are displayed as [1, 2, 3, 4, 5, 6, 0] (Mon-Sun)
    const dayOrder = [1, 2, 3, 4, 5, 6, 0];
    const startDayIndex = dayOrder.indexOf(start.day);
    const endDayIndex = dayOrder.indexOf(end.day);
    const minDayIndex = Math.min(startDayIndex, endDayIndex);
    const maxDayIndex = Math.max(startDayIndex, endDayIndex);

    const startTimeIndex = uniqueTimes.indexOf(start.time);
    const endTimeIndex = uniqueTimes.indexOf(end.time);
    const startTime = Math.min(startTimeIndex, endTimeIndex);
    const endTime = Math.max(startTimeIndex, endTimeIndex);

    for (let dayIdx = minDayIndex; dayIdx <= maxDayIndex; dayIdx++) {
      const day = dayOrder[dayIdx];
      for (let timeIdx = startTime; timeIdx <= endTime; timeIdx++) {
        const time = uniqueTimes[timeIdx];
        cells.add(`${day}_${time}`);
      }
    }

    return cells;
  };

  const applyPriceToMultipleSlots = (slotIds: Set<string>) => {
    // console.log('applyPriceToMultipleSlots called:', {
    //   selectedPricePoint,
    //   slotIdsSize: slotIds.size,
    //   slotIds: Array.from(slotIds),
    //   userRoleId
    // });

    if (!selectedPricePoint || slotIds.size === 0) return;

    // Check for price mismatches between staff and guest prices
    let mismatchFound = false;
    let firstMismatchInfo: any = null;
    let missingStaffSlots: string[] = [];

    slotIds.forEach((slotId) => {
      const [day, time] = slotId.split("_");
      const dayOfWeek = parseInt(day);

      if (userRoleId === ROLES.GUEST_USER) {
        // Guest setting price - check if staff has a price first
        const staffPricePointId = checkStaffPriceForSlot(dayOfWeek, time);

        if (!staffPricePointId) {
          // No staff price set - add to missing list
          missingStaffSlots.push(`${getDayName(dayOfWeek)} ${time}`);
        } else if (staffPricePointId !== selectedPricePoint && !mismatchFound) {
          mismatchFound = true;
          firstMismatchInfo = {
            dayOfWeek,
            time,
            timeSlotId: slotId,
            staffPricePointId,
            guestPricePointId: selectedPricePoint,
            message: "Guest price must match existing staff price",
          };
        }
      } else if (userRoleId === ROLES.BOOKING_STAFF) {
        // Staff setting price - we need to check the guestSlotPricing array
        // Find if there's a guest slot with a different price
        const timeSlotIdToCheck = `${dayOfWeek}_${time}`;
        const guestSlotForThisTime = guestSlotPricing.find(
          (gs) => gs.timeSlotId === timeSlotIdToCheck,
        );

        if (
          guestSlotForThisTime &&
          guestSlotForThisTime.pricePointId &&
          guestSlotForThisTime.pricePointId !== selectedPricePoint &&
          !mismatchFound
        ) {
          mismatchFound = true;
          firstMismatchInfo = {
            dayOfWeek,
            time,
            timeSlotId: slotId,
            staffPricePointId: selectedPricePoint,
            guestPricePointId: guestSlotForThisTime.pricePointId,
            message: "Staff price must match existing guest price",
          };
        }
      }
    });

    // Check if guest is trying to set prices without staff prices
    if (userRoleId === ROLES.GUEST_USER && missingStaffSlots.length > 0) {
      const slotsToShow = missingStaffSlots.slice(0, 5);
      const message =
        missingStaffSlots.length > 5
          ? `Staff/Admin price must be set first for ${missingStaffSlots.length} slots, including: ${slotsToShow.join(", ")}...`
          : `Staff/Admin price must be set first for: ${slotsToShow.join(", ")}`;
      toast.error(message);
      return;
    }

    if (mismatchFound && firstMismatchInfo && userRoleId === ROLES.GUEST_USER) {
      // Show dialog for guest users
      setPriceMismatchDialog({
        isOpen: true,
        slotInfo: {
          dayOfWeek: firstMismatchInfo.dayOfWeek,
          time: firstMismatchInfo.time,
          timeSlotId: firstMismatchInfo.timeSlotId,
        },
        staffPricePointId: firstMismatchInfo.staffPricePointId,
        guestPricePointId: firstMismatchInfo.guestPricePointId,
      });
      return; // Don't apply prices yet
    } else if (
      mismatchFound &&
      firstMismatchInfo &&
      userRoleId === ROLES.BOOKING_STAFF
    ) {
      // For staff, show a warning but allow them to proceed (they might need to fix mismatches)
      console.warn("⚠️ Price mismatch warning:", firstMismatchInfo.message);
      // Optionally, you could show a confirmation dialog here
    }

    // No mismatches found or not guest view, proceed with applying prices
    const newSlotPricings: SlotPricing[] = [];
    const updatedSlotPricings: SlotPricing[] = [];

    slotIds.forEach((slotId) => {
      const [day, time] = slotId.split("_");
      const dayOfWeek = parseInt(day);
      const existingSlot = getSlotPrice(dayOfWeek, time);

      if (!existingSlot) {
        // Slot doesn't exist, create new
        newSlotPricings.push({
          timeSlotId: slotId,
          pricePointId: selectedPricePoint,
          dayOfWeek: dayOfWeek,
          user_role_id: userRoleId,
        });
      } else if (existingSlot.pricePointId !== selectedPricePoint) {
        // Slot exists but has different price (including null), update it
        updatedSlotPricings.push({
          ...existingSlot,
          pricePointId: selectedPricePoint,
        });
      }
    });

    console.log("Applying prices to slots:", {
      newSlots: newSlotPricings.length,
      updatedSlots: updatedSlotPricings.length,
      totalAffected: newSlotPricings.length + updatedSlotPricings.length,
    });

    // Merge existing slots with updates and new slots
    const updatedSlots = slotPricing
      .map((slot) => {
        const update = updatedSlotPricings.find(
          (u) => u.timeSlotId === slot.timeSlotId,
        );
        return update || slot;
      })
      .concat(newSlotPricings);

    updateSlotPricing(updatedSlots);
  };

  const handleCreatePricePoint = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPriceAmount || parseFloat(newPriceAmount) <= 0) {
      return;
    }

    onPricePointCreate({
      amount: parseFloat(newPriceAmount),
      label: newPriceLabel || undefined,
      currency: venueCurrency,
      vatPercentage: parseFloat(vatPercentage) || 0,
      includeVat: includeVat,
    });

    setNewPriceAmount("");
    setNewPriceLabel("");
    setVatPercentage("25");
    setIncludeVat(true);
    setIsAddPriceModalOpen(false);
  };

  const handleDeleteConfirmation = (pricePoint: PricePoint) => {
    setDeleteConfirmation({
      isOpen: true,
      pricePoint,
    });
  };

  const confirmDelete = () => {
    if (deleteConfirmation.pricePoint) {
      onPricePointDelete(deleteConfirmation.pricePoint.id);
      setDeleteConfirmation({ isOpen: false, pricePoint: null });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, pricePoint: null });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Configuration Panel */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* User Type Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-sm">
                  Configure time slots for
                </h4>
              </div>
              <Select
                value={userRoleId.toString()}
                onValueChange={handleRoleChange}
                disabled={isSwitchingRole}
              >
                <SelectTrigger className="w-full" disabled={isSwitchingRole}>
                  <SelectValue placeholder="Select user type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ROLES.GUEST_USER.toString()}>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span>Customer/Guest view</span>
                    </div>
                  </SelectItem>
                  <SelectItem value={ROLES.BOOKING_STAFF.toString()}>
                    <div className="flex items-center gap-2">
                      <UserCog className="h-4 w-4" />
                      <span>Admin/Staff view</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {userRoleId === ROLES.GUEST_USER
                  ? "Configure time slots as they appear to customers and guests"
                  : "Configure time slots as they appear to admin and booking staff"}
              </p>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3 flex items-center justify-between">
                Price points
                <span
                  className="text-sm text-purple-600 font-normal underline cursor-pointer"
                  onClick={() => setIsAddPriceModalOpen(true)}
                >
                  + Add price
                </span>
              </h4>
              <div
                className={`space-y-2 ${pricePoints.length > 5 ? "max-h-80 overflow-y-auto pr-2" : ""}`}
              >
                {sortedPricePoints.map((pricePoint) => {
                  const originalIndex = pricePoints.findIndex(
                    (p) => p.id === pricePoint.id,
                  );
                  const colors =
                    COLOR_PALETTE[originalIndex % COLOR_PALETTE.length];
                  const statusId =
                    pricePoint.status_id || STATUS_IDS.PRICE_POINT_ACTIVE;
                  const isArchived =
                    statusId === STATUS_IDS.PRICE_POINT_ARCHIVED;

                  return (
                    <div
                      key={pricePoint.id}
                      className={`flex items-center justify-between p-2 border-2 rounded transition-all ${
                        selectedPricePoint === pricePoint.id
                          ? `${colors.bg} ${colors.border} ${colors.text} border-opacity-100`
                          : isArchived
                            ? "border-gray-200 bg-gray-50 opacity-60"
                            : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <div
                        className={`cursor-pointer flex-1 flex items-center gap-2 ${isArchived ? "cursor-not-allowed" : ""}`}
                        onClick={() =>
                          !isArchived &&
                          onPricePointSelect(
                            selectedPricePoint === pricePoint.id
                              ? null
                              : pricePoint.id,
                          )
                        }
                      >
                        <div
                          className={`w-4 h-4 rounded-full ${colors.bg} ${colors.border} border-2 ${isArchived ? "opacity-50" : ""}`}
                        />
                        <div className="flex-1">
                          <div
                            className={`text-sm font-medium ${isArchived ? "text-gray-500" : ""}`}
                          >
                            {pricePoint.pricing?.price || pricePoint.amount}{" "}
                            {getCurrencySymbol(venueCurrency || "SEK")}
                            {pricePoint.include_vat || pricePoint.includeVat
                              ? `, ${pricePoint.pricing?.vat_percentage || pricePoint.vat_percentage || pricePoint.vatPercentage}%`
                              : ""}
                          </div>
                          {(pricePoint.pricing?.label || pricePoint.label) && (
                            <div
                              className={`text-xs ${isArchived ? "text-gray-400" : "text-muted-foreground"}`}
                            >
                              {pricePoint.pricing?.label || pricePoint.label}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isArchived && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            archived
                          </span>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteConfirmation(pricePoint);
                          }}
                          className={`h-6 w-6 p-0 ${isArchived ? "opacity-50" : ""}`}
                          title={isArchived ? "Reactivate" : "Archive"}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 text-sm text-muted-foreground">
                <p>
                  <strong>Instructions:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Select a price point from above</li>
                  <li>Click on time slots to assign the selected price</li>
                  <li>
                    <strong>Drag to select multiple slots</strong>
                  </li>
                  <li>Click assigned slots to remove pricing</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Time Slot Grid */}
        <Card className="lg:col-span-3 relative">
          {/* Loading overlay when switching roles */}
          {isSwitchingRole && (
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                <p className="text-sm text-gray-600">
                  Loading{" "}
                  {userRoleId === ROLES.GUEST_USER ? "customer" : "admin"}{" "}
                  pricing...
                </p>
              </div>
            </div>
          )}
          <CardHeader>
            <div className="space-y-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  Time slot pricing
                  {isLoadingSlots && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  <Badge variant="secondary" className="ml-2">
                    {userRoleId === ROLES.GUEST_USER ? (
                      <>
                        <Users className="h-3 w-3 mr-1" /> Customer/Guest view (
                        {slotPricing.length})
                      </>
                    ) : (
                      <>
                        <UserCog className="h-3 w-3 mr-1" /> Admin/Staff view (
                        {slotPricing.length})
                      </>
                    )}
                  </Badge>
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {loadingError ? (
                    <span className="text-red-600">Error: {loadingError}</span>
                  ) : isLoadingSlots ? (
                    "Loading time slots..."
                  ) : selectedPricePoint ? (
                    "Click cells to assign the selected price. Click and drag to select multiple cells."
                  ) : (
                    "Select a price point to start assigning prices to time slots."
                  )}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="border p-2 text-left bg-muted font-medium min-w-16">
                      Time
                    </th>
                    {[1, 2, 3, 4, 5, 6, 0].map((dayIndex) => (
                      <th
                        key={dayIndex}
                        className="border p-2 text-center bg-muted font-medium min-w-20"
                      >
                        <div className="text-sm font-medium">{DAYS_OF_WEEK[dayIndex]}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody
                  onMouseLeave={() => {
                    // Don't cancel drag on mouseLeave - let onMouseUp handle it
                    // This prevents accidental cancellation when dragging quickly
                  }}
                >
                  {uniqueTimes.map((time) => (
                    <tr key={time}>
                      <td className="border p-2 text-sm font-medium bg-muted">
                        {formatTimeDisplay(time, venueDefaultLanguage)}
                      </td>
                      {[1, 2, 3, 4, 5, 6, 0].map((dayOfWeek) => {
                        const slotPrice = getSlotPrice(dayOfWeek, time);
                        const hasValidPrice =
                          slotPrice && slotPrice.pricePointId !== null;
                        const pricePoint = hasValidPrice
                          ? pricePoints.find(
                              (p) => p.id === slotPrice.pricePointId,
                            )
                          : null;

                        const pricePointIndex = pricePoint
                          ? pricePoints.findIndex((p) => p.id === pricePoint.id)
                          : -1;
                        const priceColors =
                          pricePointIndex >= 0
                            ? COLOR_PALETTE[
                                pricePointIndex % COLOR_PALETTE.length
                              ]
                            : null;
                        const selectedColors = selectedPricePoint
                          ? COLOR_PALETTE[
                              pricePoints.findIndex(
                                (p) => p.id === selectedPricePoint,
                              ) % COLOR_PALETTE.length
                            ]
                          : null;
                        const isInDragSelection = dragSelection.has(
                          `${dayOfWeek}_${time}`,
                        );
                        const timeSlotId = `${dayOfWeek}_${time}`;
                        const isSavingThisSlot = savingSlots.has(timeSlotId);

                        let cellClassName =
                          "border p-1 text-center transition-all relative select-none ";

                        if (isInDragSelection && selectedColors) {
                          cellClassName += `${selectedColors.bg} ${selectedColors.border} border-2 opacity-75`;
                        } else if (hasValidPrice && priceColors) {
                          cellClassName += `${priceColors.bg} ${priceColors.border} border-2 cursor-pointer`;
                        } else if (selectedPricePoint) {
                          cellClassName +=
                            "bg-gray-50 hover:bg-gray-100 cursor-crosshair";
                        } else {
                          cellClassName += "hover:bg-gray-50 cursor-pointer";
                        }

                        return (
                          <td
                            key={dayOfWeek}
                            className={cellClassName}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              if (selectedPricePoint) {
                                setIsDragging(true);
                                setDragStartCell({ day: dayOfWeek, time });
                                setDragSelection(
                                  new Set([`${dayOfWeek}_${time}`]),
                                );
                              }
                            }}
                            onMouseEnter={() => {
                              // Extend drag selection as user drags across cells
                              if (isDragging && dragStartCell && selectedPricePoint) {
                                // Get the index of the start time and current time
                                const startTimeIndex = uniqueTimes.indexOf(
                                  dragStartCell.time,
                                );
                                const currentTimeIndex = uniqueTimes.indexOf(time);
                                const minTimeIndex = Math.min(
                                  startTimeIndex,
                                  currentTimeIndex,
                                );
                                const maxTimeIndex = Math.max(
                                  startTimeIndex,
                                  currentTimeIndex,
                                );

                                // Get all days in the order they appear in the table
                                const dayOrder = [1, 2, 3, 4, 5, 6, 0]; // Mon-Sun
                                const startDayIndex = dayOrder.indexOf(
                                  dragStartCell.day,
                                );
                                const endDayIndex = dayOrder.indexOf(dayOfWeek);
                                const minDayIndex = Math.min(
                                  startDayIndex,
                                  endDayIndex,
                                );
                                const maxDayIndex = Math.max(
                                  startDayIndex,
                                  endDayIndex,
                                );

                                // Build the new selection set
                                const newSelection = new Set<string>();
                                for (let di = minDayIndex; di <= maxDayIndex; di++) {
                                  const d = dayOrder[di];
                                  for (let t = minTimeIndex; t <= maxTimeIndex; t++) {
                                    newSelection.add(
                                      `${d}_${uniqueTimes[t]}`,
                                    );
                                  }
                                }

                                setDragSelection(newSelection);
                              }
                            }}
                            onMouseUp={() => {
                              if (isDragging && dragSelection.size > 0) {
                                applyPriceToMultipleSlots(dragSelection);
                                setIsDragging(false);
                                setDragSelection(new Set());
                                setDragStartCell(null);
                              } else if (!isDragging) {
                                handleCellClick(dayOfWeek, time);
                              }
                            }}
                          >
                            {isSavingThisSlot ? (
                              <div className="h-6 w-full flex items-center justify-center">
                                <Loader2 className="h-3 w-3 animate-spin text-gray-600" />
                              </div>
                            ) : hasValidPrice ? (
                              <div
                                className="relative h-6 w-full"
                                title={
                                  pricePoint
                                    ? formatPrice(
                                        pricePoint.pricing?.price ||
                                          pricePoint.amount ||
                                          0,
                                        venueCurrency,
                                      )
                                    : ""
                                }
                              >
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-xs opacity-0 hover:opacity-100 text-gray-700">
                                    ×
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="h-6 w-full flex items-center justify-center">
                                {(selectedPricePoint || isInDragSelection) && (
                                  <span className="text-xs text-gray-400">
                                    {isInDragSelection ? "✓" : "+"}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Price Modal */}
      <Dialog open={isAddPriceModalOpen} onOpenChange={setIsAddPriceModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add new price point</DialogTitle>
            <DialogDescription>
              Create a new price point that can be assigned to time slots.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreatePricePoint} className="space-y-4">
            <div>
              <Label htmlFor="price-amount" className="text-sm font-medium">
                Amount ({getCurrencySymbol(venueCurrency || "SEK")})
              </Label>
              <Input
                id="price-amount"
                placeholder="150"
                value={newPriceAmount}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, "");
                  setNewPriceAmount(value);
                }}
                type="number"
                step="0.01"
                min="0"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter number only (e.g., 150)
              </p>
            </div>

            <div>
              <Label htmlFor="vat-percentage" className="text-sm font-medium">
                VAT percentage (%)
              </Label>
              <Input
                id="vat-percentage"
                placeholder="25"
                value={vatPercentage}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9.]/g, "");
                  setVatPercentage(value);
                }}
                type="number"
                step="0.1"
                min="0"
                max="100"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter VAT % (e.g., 25 for 25%)
              </p>
            </div>

            <div>
              <Label htmlFor="price-label" className="text-sm font-medium">
                Label (optional)
              </Label>
              <Input
                id="price-label"
                placeholder="e.g., Peak hours, Standard rate"
                value={newPriceLabel}
                onChange={(e) => setNewPriceLabel(e.target.value)}
                className="mt-1"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddPriceModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!newPriceAmount || parseFloat(newPriceAmount) <= 0}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add price point
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmation.isOpen}
        onOpenChange={(open) => !open && cancelDelete()}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-orange-600">
              Archive price point
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to archive this price point? It will no
              longer be available for selection but can be reactivated later.
            </DialogDescription>
          </DialogHeader>

          {deleteConfirmation.pricePoint && (
            <div className="py-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Trash2 className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-orange-800 mb-2">
                      Price point details:
                    </h3>
                    <div className="text-sm text-orange-700 space-y-1">
                      <div>
                        • Amount: {deleteConfirmation.pricePoint.amount}{" "}
                        {getCurrencySymbol(venueCurrency || "SEK")}
                      </div>
                      {deleteConfirmation.pricePoint.label && (
                        <div>
                          • Label: {deleteConfirmation.pricePoint.label}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={cancelDelete}>
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Archive price point
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Price Mismatch Dialog */}
      <Dialog
        open={priceMismatchDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setPriceMismatchDialog({
              isOpen: false,
              slotInfo: null,
              staffPricePointId: null,
              guestPricePointId: null,
            });
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Price mismatch detected
            </DialogTitle>
            <DialogDescription>
              This time slot already has a staff price set. Guest prices must
              match staff prices to ensure consistency.
            </DialogDescription>
          </DialogHeader>

          {priceMismatchDialog.slotInfo && (
            <div className="py-4 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Time slot:</span>
                    <span className="text-sm">
                      {DAYS_OF_WEEK[priceMismatchDialog.slotInfo.dayOfWeek]} at{" "}
                      {priceMismatchDialog.slotInfo.time}
                    </span>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <UserCog className="h-4 w-4" />
                        Staff price:
                      </span>
                      <span className="text-sm font-medium">
                        {pricePoints.find(
                          (p) => p.id === priceMismatchDialog.staffPricePointId,
                        )?.pricing?.price ||
                          pricePoints.find(
                            (p) =>
                              p.id === priceMismatchDialog.staffPricePointId,
                          )?.amount ||
                          "N/A"}{" "}
                        {getCurrencySymbol(venueCurrency || "SEK")}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Guest price (selected):
                      </span>
                      <span className="text-sm font-medium">
                        {pricePoints.find(
                          (p) => p.id === priceMismatchDialog.guestPricePointId,
                        )?.pricing?.price ||
                          pricePoints.find(
                            (p) =>
                              p.id === priceMismatchDialog.guestPricePointId,
                          )?.amount ||
                          "N/A"}{" "}
                        {getCurrencySymbol(venueCurrency || "SEK")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                <p className="font-medium mb-1">Policy:</p>
                <p>
                  Guest prices must match staff prices. Click "Use Staff Price"
                  to apply the correct price to this guest time slot.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                // Close dialog and don't apply the price
                setPriceMismatchDialog({
                  isOpen: false,
                  slotInfo: null,
                  staffPricePointId: null,
                  guestPricePointId: null,
                });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // Apply the staff price to the guest slot
                if (
                  priceMismatchDialog.slotInfo &&
                  priceMismatchDialog.staffPricePointId
                ) {
                  const { dayOfWeek, time, timeSlotId } =
                    priceMismatchDialog.slotInfo;

                  // Check if slot already exists
                  const existingSlot = slotPricing.find(
                    (sp) => sp.timeSlotId === timeSlotId,
                  );

                  let updatedSlots: SlotPricing[];
                  if (existingSlot) {
                    // Update existing slot
                    updatedSlots = slotPricing.map((sp) =>
                      sp.timeSlotId === timeSlotId
                        ? {
                            ...sp,
                            pricePointId: priceMismatchDialog.staffPricePointId,
                          }
                        : sp,
                    );
                  } else {
                    // Create new slot
                    const newSlotPricing: SlotPricing = {
                      timeSlotId,
                      pricePointId: priceMismatchDialog.staffPricePointId,
                      dayOfWeek,
                      user_role_id: userRoleId,
                    };
                    updatedSlots = [...slotPricing, newSlotPricing];
                  }

                  updateSlotPricing(updatedSlots);

                  // Also select the staff price point for future selections
                  onPricePointSelect(priceMismatchDialog.staffPricePointId);
                }
                setPriceMismatchDialog({
                  isOpen: false,
                  slotInfo: null,
                  staffPricePointId: null,
                  guestPricePointId: null,
                });
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Check className="h-4 w-4 mr-2" />
              Use staff price
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
